import * as React from 'react'
import ReactMarkdown, { Components } from 'react-markdown'
import { JsxRuntimeComponents } from 'react-markdown/lib'
import root from 'react-shadow'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark } from 'react-syntax-highlighter/dist/cjs/styles/prism'
import rehypeKatex from 'rehype-katex'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import remarkToc from 'remark-toc'
import 'katex'

import 'katex/dist/katex.min.css'

import { cn } from '@/lib/utils'
import { useImagePreview } from '@/hooks/use-image-preview'

import { ImagePreviewModal } from '@/components/common/image-preview-modal'
import AudioPhraseSpan from '@/components/markdown/audio-phrase-span'
import SectionAudioPlayer from '@/components/markdown/section-audio-player'

import {
  getAudioPhraseHint,
  getAudioPhraseUrl,
  getSectionAudioUrlFromNode,
  HastLikeNode,
  headingHasSectionAudio,
  isAudioPhraseNode,
  isHeadingInlineAudio,
  isSectionAudioRow,
} from '@/utils/audio-phrase-markdown'
import { createHeadingId, extractHeadingTextFromHastNode } from '@/utils/heading-id-generate'
import { extractMarkdownEmbeddedStyles } from '@/utils/markdown-embedded-styles'
import { convertMarkdownUnderlineMarkers } from '@/utils/markdown-underline'

const MarkdownComponents: Partial<JsxRuntimeComponents> = {
  code({ node, className, ...props }: any) {
    const hasLang = /language-(\w+)/.exec(className || '')

    return hasLang ? (
      <SyntaxHighlighter
        language={hasLang[1]}
        PreTag="div"
        className="codeStyle"
        showLineNumbers={true}
        useInlineStyles={true}
        style={oneDark}
      >
        {String(props.children).replace(/\n$/, '')}
      </SyntaxHighlighter>
    ) : (
      <code className={cn(className, 'code')} {...props}>
        {String(props.children).replace(/\n$/, '')}
      </code>
    )
  },
  a: ({ node, className, ...props }: any) => {
    return <a className={cn(className)} {...props} target="_blank" rel="noreferrer noopener" />
  },
  span: ({ node, children, ...props }) => {
    if (node?.properties?.dataMentionId) {
      return (
        <span
          className="mention"
          style={{
            fontWeight: 700,
            color: '#000',
          }}
          {...props}
        >
          {children}
        </span>
      )
    }
    if (isAudioPhraseNode(node?.properties as Record<string, unknown> | undefined)) {
      const properties = node?.properties as Record<string, unknown> | undefined
      return (
        <AudioPhraseSpan
          audioUrl={getAudioPhraseUrl(properties)}
          hint={getAudioPhraseHint(properties) || undefined}
        >
          {children}
        </AudioPhraseSpan>
      )
    }
    return <span {...props}>{children}</span>
  },
  div: ({ node, children, ...props }) => {
    const properties = node?.properties as Record<string, unknown> | undefined
    if (isSectionAudioRow(properties)) {
      const audioUrl = getSectionAudioUrlFromNode(node as HastLikeNode)
      if (audioUrl) {
        return <SectionAudioPlayer audioUrl={audioUrl} />
      }
    }
    return <div {...props}>{children}</div>
  },
  audio: ({ node, ...props }) => {
    const properties = node?.properties as Record<string, unknown> | undefined
    const src = properties?.src
    if (typeof src === 'string' && isHeadingInlineAudio(properties)) {
      return <SectionAudioPlayer audioUrl={src} />
    }
    return <audio {...props} />
  },
  p: ({ children, node, ...props }) => {
    // Check if paragraph is empty or contains only non-breaking spaces
    const content = React.Children.toArray(children).join('').trim()
    if (content === '\u00a0' || content === '') {
      return null
    }
    return <p {...props}>{children}</p>
  },
  u: ({ children, ...props }) => (
    <u className="underline underline-offset-2" {...props}>
      {children}
    </u>
  ),
  style: ({ node, ...props }) => {
    const css =
      node?.children
        ?.map((child: { type?: string; value?: string }) => (child.type === 'text' ? child.value || '' : ''))
        .join('') ?? ''
    if (!css.trim()) return null
    return <style {...props} dangerouslySetInnerHTML={{ __html: css }} />
  },
}

function MarkdownPreview({
  children,
  components,
  enableToc = false,
  enableImagePreview = false,
  allowRawHtml = true,
  className,
}: {
  children: string
  components?: Partial<Components> | null
  enableToc?: boolean
  enableImagePreview?: boolean
  allowRawHtml?: boolean
  className?: string
}) {
  const { isOpen, currentImage, openPreview, closePreview } = useImagePreview()
  const contentWithTocHeading = enableToc ? `## Table of Contents\n\n${children}` : children
  const contentWithUnderline = React.useMemo(
    () => convertMarkdownUnderlineMarkers(contentWithTocHeading),
    [contentWithTocHeading]
  )
  const { styles: embeddedStyles, content: markdownWithoutStyles } = React.useMemo(
    () => extractMarkdownEmbeddedStyles(contentWithUnderline),
    [contentWithUnderline]
  )

  const createMarkdownComponents = React.useCallback((): Partial<JsxRuntimeComponents> => {
    const baseComponents = {
      ...MarkdownComponents,
      h1: ({ node, className, ...props }: any) => {
        const id = createHeadingId(extractHeadingTextFromHastNode(node))
        return (
          <h1
            id={id}
            className={cn(headingHasSectionAudio(node) && 'heading-with-audio', className)}
            {...props}
          />
        )
      },
      h2: ({ node, className, ...props }: any) => {
        const id = createHeadingId(extractHeadingTextFromHastNode(node))
        return (
          <h2
            id={id}
            className={cn(headingHasSectionAudio(node) && 'heading-with-audio', className)}
            {...props}
          />
        )
      },
      h3: ({ node, className, ...props }: any) => {
        const id = createHeadingId(extractHeadingTextFromHastNode(node))
        return (
          <h3
            id={id}
            className={cn(headingHasSectionAudio(node) && 'heading-with-audio', className)}
            {...props}
          />
        )
      },
    }

    if (enableImagePreview) {
      baseComponents.img = ({ node, src, alt, ...props }) => {
        if (!src) return null

        return (
          <div className="mt-4 flex justify-start">
            <div
              className="relative inline-block cursor-pointer rounded-lg overflow-hidden group max-w-full"
              onClick={e => {
                e.preventDefault()
                openPreview(src, alt || 'Image')
              }}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  openPreview(src, alt || 'Image')
                }
              }}
              aria-label={`Open image preview: ${alt || 'Image'}`}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={src}
                alt={alt || 'Image'}
                className="w-full object-cover block rounded-lg h-auto max-w-full"
                style={{
                  margin: 0,
                }}
              />
              <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <span className="sr-only">Click to zoom</span>
              </div>
            </div>
          </div>
        )
      }
    }

    return { ...baseComponents, ...components }
  }, [enableImagePreview, openPreview, components])

  return (
    <>
      {embeddedStyles ? <style dangerouslySetInnerHTML={{ __html: embeddedStyles }} /> : null}
      <ReactMarkdown
        components={createMarkdownComponents()}
        remarkPlugins={[remarkMath, remarkGfm, ...(enableToc ? [remarkToc] : [])]}
        rehypePlugins={[rehypeKatex, ...(allowRawHtml ? [rehypeRaw] : [])]}
        className={cn('pre-wrap-code', className)}
      >
        {markdownWithoutStyles}
      </ReactMarkdown>

      {enableImagePreview && currentImage && (
        <ImagePreviewModal
          src={currentImage.src}
          alt={currentImage.alt}
          isOpen={isOpen}
          onClose={closePreview}
          showDownload={true}
        />
      )}
    </>
  )
}

function CustomCssMarkdownPreview({
  children,
  components,
}: {
  children: string
  components?: Partial<Components> | null
}) {
  const { styles: embeddedStyles, content } = React.useMemo(
    () => extractMarkdownEmbeddedStyles(convertMarkdownUnderlineMarkers(children.replace(/<p>\s*&nbsp;\s*<\/p>/gi, ''))),
    [children]
  )

  return (
    <root.div>
      {embeddedStyles ? <style dangerouslySetInnerHTML={{ __html: embeddedStyles }} /> : null}
      <style>
        {`
          /* Enhanced custom styles for shadow DOM */
          .mention {
            font-weight: 700 !important;
            color: #3b82f6 !important;
            background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%) !important;
            padding: 0.125em 0.375em !important;
            border-radius: 4px !important;
            border: 1px solid #93c5fd !important;
            transition: all 0.2s ease !important;
          }
          
          .mention:hover {
            background: linear-gradient(135deg, #bfdbfe 0%, #93c5fd 100%) !important;
            transform: translateY(-1px) !important;
            box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2) !important;
          }
          
          img {
            max-width: 100% !important;
            height: auto !important;
            border-radius: 12px !important;
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
            transition: all 0.3s ease !important;
            filter: brightness(1) contrast(1) !important;
          }
          
          img:hover {
            transform: scale(1.02) !important;
            box-shadow: 0 12px 35px rgba(0, 0, 0, 0.15) !important;
            filter: brightness(1.05) contrast(1.05) !important;
          }
          
          /* Enhanced typography for shadow DOM */
          * {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif !important;
          }
          
          /* Smooth scrolling */
          html {
            scroll-behavior: smooth !important;
          }
          
          /* Selection styling */
          ::selection {
            background: rgba(59, 130, 246, 0.2) !important;
            color: inherit !important;
          }
          
          ::-moz-selection {
            background: rgba(59, 130, 246, 0.2) !important;
            color: inherit !important;
          }
          
          /* improve style of table */
          table {
            width: 100%;
            border: 1px solid #e5e5e5;
          }
          
          th, td {
            padding: 8px 14px !important;
            border: 1px solid #e5e5e5;
            text-align: start;
          }
        `}
      </style>
      <ReactMarkdown
        components={{ ...MarkdownComponents, ...components }}
        remarkPlugins={[remarkToc, remarkMath, remarkGfm]}
        rehypePlugins={[rehypeKatex, rehypeRaw]}
        className="pre-wrap-code"
      >
        {content}
      </ReactMarkdown>
    </root.div>
  )
}

export default MarkdownPreview
export { CustomCssMarkdownPreview }

/* 
eslint @typescript-eslint/ban-ts-comment: 0, 
@typescript-eslint/no-explicit-any: 0,
unused-imports/no-unused-vars: 0
*/
